import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationModalProps } from '~/Components';
import { extractErrorMessage } from '~/Helpers/Error.helper';
import { ToastVariant, useToastMessage } from '~/Hooks';
import { IStaff } from '~/Models/DTO/Staff.model';
import { IMenuAction } from '~/Models/interfaces/menuAction.models';
import { useRequestDeactivateStaff } from '~/requests';
import { StaffDeactivationDeletion } from './StaffDeactivationDeletion';

export const useStaffDeactivationModal: ({
  setConfirmationModalProps,
}: {
  setConfirmationModalProps: React.Dispatch<React.SetStateAction<ConfirmationModalProps>>;
}) => (item: IStaff) => IMenuAction = ({ setConfirmationModalProps }) => {
  const { t } = useTranslation();
  const { showToast } = useToastMessage();
  const {
    mutate: activateStaff,
    isLoading: isActivating,
    isError,
    error,
    isSuccess,
  } = useRequestDeactivateStaff();

  useEffect(() => {
    if (isError) {
      showToast('', extractErrorMessage(error), ToastVariant.ERROR, true);
    }
  });

  useEffect(() => {
    if (isSuccess) {
      setConfirmationModalProps((prev) => ({
        ...prev,
        open: false,
      }));
    }
  }, [isSuccess]);

  useEffect(() => {
    setConfirmationModalProps((prev) => ({
      ...prev,
      confirm: {
        ...prev.confirm,
        isLoading: isActivating,
      },
    }));
  }, [isActivating]);

  const DeactivationContent = (item: IStaff) => (
    <StaffDeactivationDeletion staff={item} setConfirmationModalProps={setConfirmationModalProps} />
  );

  const onClick: (staff: IStaff) => IMenuAction['onClick'] = (staff: IStaff) => () => {
    if (staff.disabled) {
      setConfirmationModalProps((prev) => ({ ...prev, open: false }));
      activateStaff({ staffId: staff.id, disabled: false });
    } else {
      setConfirmationModalProps({
        open: true,
        title: t('app.deactivate'),
        children: DeactivationContent(staff),
        cancel: { title: t('app.cancel') },
        confirm: {
          title: t('app.deactivate'),
          disabled: true,
          isLoading: true,
          color: 'error',
        },
      });
    }
  };

  return (item: IStaff) => ({
    name: item.disabled ? t('app.activate') : t('app.deactivate'),
    onClick: onClick(item),
  });
};
